import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/base/SectionHeading";

type SortKey = "latest" | "hottest";

const filterChips = [{ key: "all", name: "全部" }, ...categories.map((c) => ({ key: c.key, name: c.name }))];

export default function LatestSection() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      setChip("all");
    }
  }, [searchParams]);

  const list = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const result = articles.filter((a) => {
      const matchChip = chip === "all" || a.category === chip;
      if (!matchChip) return false;
      if (!keyword) return true;
      const haystack = [a.title, a.excerpt, a.categoryName, ...a.tags].join(" ").toLowerCase();
      return haystack.includes(keyword);
    });
    return [...result].sort((a, b) =>
      sort === "hottest" ? b.heat - a.heat : b.date.localeCompare(a.date),
    );
  }, [query, chip, sort]);

  return (
    <section id="latest" className="scroll-mt-28 border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Latest"
          title="最新资讯"
          description="按标签筛选或直接搜索关键词，找到你关心的娱乐话题。"
        />

        <div className="mb-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
            <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="输入标题、标签或栏目关键词…"
              aria-label="筛选资讯"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="清空搜索"
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-500 hover:bg-background-200"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {filterChips.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setChip(item.key)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  chip === item.key
                    ? "bg-secondary-600 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.name}
              </button>
            ))}
            <span className="mx-1 hidden h-5 w-px bg-background-300 sm:block"></span>
            <div className="flex items-center rounded-full border border-background-300 p-0.5">
              <button
                type="button"
                onClick={() => setSort("latest")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  sort === "latest" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setSort("hottest")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  sort === "hottest" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <p className="mb-5 text-sm text-foreground-500" role="status">
          共找到 <span className="font-medium text-primary-700">{list.length}</span> 条内容
        </p>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-background-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600">没有匹配的内容，换个关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setChip("all");
              }}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}